import { motion } from 'motion/react';
import { 
  GraduationCap, 
  Scale, 
  Home, 
  Sparkles, 
  Heart, 
  TrendingUp, 
  Coffee, 
  Crown,
  ArrowRight,
  Users,
  Briefcase,
  Star
} from 'lucide-react';
import { useTranslation } from '../i18n';


interface ServicesOverviewProps {
  onNavigateHome: () => void;
  onNavigateToSchoolSupport?: () => void;
  onNavigateToLegalSupport?: () => void;
  onNavigateToRealEstate?: () => void;
  onNavigateToHomeManagement?: () => void;
  onNavigateToSpiritualWellness?: () => void;
  onNavigateToBusinessConsulting?: () => void;
  onNavigateToBeverageDevelopment?: () => void;
  onNavigateToEliteConcierge?: () => void;
  onNavigateToForFamilies?: () => void;
  onNavigateToForEntrepreneurs?: () => void;
  onNavigateToForHighNetWorth?: () => void;
}

export function ServicesOverview({ 
  onNavigateHome, 
  onNavigateToSchoolSupport,
  onNavigateToLegalSupport,
  onNavigateToRealEstate,
  onNavigateToHomeManagement,
  onNavigateToSpiritualWellness,
  onNavigateToBusinessConsulting,
  onNavigateToBeverageDevelopment,
  onNavigateToEliteConcierge,
  onNavigateToForFamilies,
  onNavigateToForEntrepreneurs,
  onNavigateToForHighNetWorth
}: ServicesOverviewProps) {
  const { t } = useTranslation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Services data with translation keys
  const servicesData = [
    {
      id: 1,
      icon: GraduationCap,
      titleKey: 'servicesOverview.services.schoolFamily.title',
      descriptionKey: 'servicesOverview.services.schoolFamily.description',
      pricingKey: 'servicesOverview.services.schoolFamily.pricing',
      navigationFunction: onNavigateToSchoolSupport,
    },
    {
      id: 2,
      icon: Scale,
      titleKey: 'servicesOverview.services.legal.title',
      descriptionKey: 'servicesOverview.services.legal.description',
      pricingKey: 'servicesOverview.services.legal.pricing',
      navigationFunction: onNavigateToLegalSupport,
    },
    {
      id: 3,
      icon: Home,
      titleKey: 'servicesOverview.services.realEstate.title',
      descriptionKey: 'servicesOverview.services.realEstate.description',
      pricingKey: 'servicesOverview.services.realEstate.pricing',
      navigationFunction: onNavigateToRealEstate,
    },
    {
      id: 4,
      icon: Sparkles,
      titleKey: 'servicesOverview.services.homeManagement.title',
      descriptionKey: 'servicesOverview.services.homeManagement.description',
      pricingKey: 'servicesOverview.services.homeManagement.pricing',
      navigationFunction: onNavigateToHomeManagement,
    },
    {
      id: 5,
      icon: Heart,
      titleKey: 'servicesOverview.services.spiritual.title',
      descriptionKey: 'servicesOverview.services.spiritual.description',
      pricingKey: 'servicesOverview.services.spiritual.pricing',
      navigationFunction: onNavigateToSpiritualWellness,
    },
    {
      id: 6,
      icon: TrendingUp,
      titleKey: 'servicesOverview.services.business.title',
      descriptionKey: 'servicesOverview.services.business.description',
      pricingKey: 'servicesOverview.services.business.pricing',
      navigationFunction: onNavigateToBusinessConsulting,
    },
    {
      id: 7,
      icon: Coffee,
      titleKey: 'servicesOverview.services.beverage.title',
      descriptionKey: 'servicesOverview.services.beverage.description',
      pricingKey: 'servicesOverview.services.beverage.pricing',
      navigationFunction: onNavigateToBeverageDevelopment,
    },
    {
      id: 8,
      icon: Crown,
      titleKey: 'servicesOverview.services.elite.title',
      descriptionKey: 'servicesOverview.services.elite.description',
      pricingKey: 'servicesOverview.services.elite.pricing',
      navigationFunction: onNavigateToEliteConcierge,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <section className="relative bg-gradient-to-br from-[#0A1A33] via-[#0A1A33] to-[#0A1A33]/95 pt-32 pb-20 px-6">
        <div className="max-w-[1280px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-white mb-6 font-bold text-[40px]">
              {t('servicesOverview.hero.title')}
            </h1>
            <p className="text-white/80 max-w-3xl mx-auto text-lg text-[16px]">
              {t('servicesOverview.hero.description')}
            </p>
          </motion.div>
        </div>

        {/* Decorative element */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C6A667]/50 to-transparent" />
      </section>

      {/* Services Grid Section */}
      <section className="py-20 px-6">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service, index) => {
              const IconComponent = service.icon;
              const isClickable = !!service.navigationFunction;
              
              const handleCardClick = () => {
                if (service.navigationFunction) {
                  service.navigationFunction();
                }
              };

              const handleLearnMoreClick = (e: React.MouseEvent) => {
                if (service.navigationFunction) {
                  e.stopPropagation();
                  service.navigationFunction();
                }
              };
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  onClick={handleCardClick}
                  className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#C6A667] flex flex-col ${
                    isClickable ? 'cursor-pointer' : ''
                  }`}
                >
                  {/* Gold accent line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C6A667]/0 via-[#C6A667] to-[#C6A667]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-t-2xl" />
                  
                  <div className="flex items-start space-x-4 mb-4 min-h-[60px]">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#C6A667] to-[#C6A667]/80 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[#0A1A33] mb-3 text-[16px] font-bold">
                        {t(service.titleKey)}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {t(service.descriptionKey)}
                  </p>

                  <div className="mb-6 pb-4 border-b border-gray-100">
                    <span className="text-sm text-gray-500">{t('servicesOverview.pricing.label')}</span>
                    <span className="text-[#C6A667] font-semibold">{t(service.pricingKey)}</span>
                  </div>

                  <button 
                    onClick={handleLearnMoreClick}
                    className="group/btn inline-flex items-center text-[#C6A667] hover:text-[#0A1A33] transition-colors duration-300 mt-auto"
                  >
                    <span className="relative font-bold font-normal">
                      {t('servicesOverview.cta.learnMore')}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-[#C6A667] group-hover/btn:w-full transition-all duration-300" />
                    </span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 px-6">
        <div className="max-w-[1280px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative bg-gradient-to-br from-[#0A1A33] via-[#0A1A33] to-[#0A1A33]/90 rounded-3xl p-12 md:p-16 overflow-hidden"
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#C6A667]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C6A667]/10 rounded-full blur-3xl" />
            
            <div className="relative text-center">
              <h2 className="text-white mb-6 font-bold text-[24px]">
                {t('servicesOverview.cta.title')}
              </h2>
              <p className="text-white/80 mb-8 max-w-2xl mx-auto text-lg text-[16px]">
                {t('servicesOverview.cta.description')}
              </p>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="group/cta px-8 py-4 bg-gradient-to-r from-[#C6A667] to-[#C6A667]/90 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <span className="flex items-center">
                  {t('servicesOverview.cta.button')}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover/cta:translate-x-1 transition-transform duration-300" />
                </span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
